// Shared mapping between the `AuditAction` enum (defined in the Prisma schema
// at apps/api/prismas/prisma/models/auditLog.prisma) and the higher-level
// categories surfaced in the Activity Log filter bar. Kept in `@common` so the
// API router and the internal-portal UI agree on the grouping.
//
// NOTE: the keys here are the string values of the Prisma `AuditAction` enum.
// When a new enum value is added, add it here too (the API maps an unknown
// action to the 'Other' category at runtime).

export const AUDIT_ACTION_CATEGORIES = [
  'Eligibility',
  'Notes',
  'Products',
  'Commissions',
  'Lifecycle',
  'Billing',
  'Configuration',
  'Other',
] as const;

export type AuditActionCategory = (typeof AUDIT_ACTION_CATEGORIES)[number];

export const AUDIT_ACTION_TO_CATEGORY: Record<string, AuditActionCategory> = {
  CREATED: 'Eligibility',
  UPDATED: 'Eligibility',
  FIELD_UPDATED: 'Eligibility',
  BATCH_UPDATE_APPLIED: 'Eligibility',
  TAG_ADDED: 'Eligibility',
  TAG_REMOVED: 'Eligibility',

  NOTE_ADDED: 'Notes',
  NOTE_UPDATED: 'Notes',
  NOTE_DELETED: 'Notes',

  PRODUCT_ADDED: 'Products',
  PRODUCT_TERMINATED: 'Products',

  COMMISSION_ADDED: 'Commissions',
  COMMISSION_UPDATED: 'Commissions',
  COMMISSION_DELETED: 'Commissions',
  COMMISSIONS_COPIED: 'Commissions',

  MEMBER_TERMINATED: 'Lifecycle',
  MEMBER_REACTIVATED: 'Lifecycle',
  GROUP_REASSIGNED: 'Lifecycle',
  DEPENDENT_ADDED: 'Lifecycle',
  STATUS_CHANGED: 'Lifecycle',

  PAYOUT_STATUS_CHANGED: 'Billing',
  DISCREPANCY_RESOLVED: 'Billing',

  // One shared event class for every governance surface: feature flags
  // (tenant + internal), admin settings, roles, and the permission catalog.
  CONFIGURATION_CHANGED: 'Configuration',
};

export function categoryForAuditAction(action: string): AuditActionCategory {
  return AUDIT_ACTION_TO_CATEGORY[action] ?? 'Other';
}

/** All `AuditAction` values that map to a given category (for BE `where` filters). */
export function auditActionsForCategory(category: AuditActionCategory): string[] {
  return Object.entries(AUDIT_ACTION_TO_CATEGORY)
    .filter(([, c]) => c === category)
    .map(([action]) => action);
}
